import type { ChannelNumber } from '../../types';
import type { DataType } from '../Analyser';
import type { VisualizerParams, GraphicsStyles, SpectrumScale } from './Visualizer';

import { Visualizer } from './Visualizer';

export type SpectrogramParams = VisualizerParams & {
  type?: DataType,
  size?: number,
  scale?: SpectrumScale,
  duration?: number,
  plotInterval?: number,
  linearFrequencyTextInterval?: number,
  timeTextInterval?: number,
  logarithmicFrequencies?: number[],
  readonly minFrequency?: number,
  readonly maxFrequency?: number
};

/**
 * This private class visualizes spectrogram.
 */
export class Spectrogram extends Visualizer {
  private type: DataType = 'uint';

  private scale: SpectrumScale = 'logarithmic';

  private duration = 10;

  private plotInterval = 4;
  private linearFrequencyTextInterval = 8;
  private timeTextInterval = 16;
  private timeOffset = 1;
  private numberOfSamples = 0;

  private renderSize = 64;

  // for logarithmic
  private logarithmicFrequencies: number[] = [32, 62.5, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];
  private minFrequency = this.logarithmicFrequencies[0];
  private maxFrequency = this.logarithmicFrequencies[this.logarithmicFrequencies.length - 1];
  private ratio = this.maxFrequency / this.minFrequency;

  private imagedata: ImageData | null = null;

  private colorFromNumber: ((data: Uint8Array[0] | Float32Array[0]) => string) | null = null;

  /**
   * This function maps unsigned int 8 bits to color string by Jet colormap.
   * @param {Uint8Array[0]|Float32Array[0]} data This argument is converted to color string based on Jet colormap.
   * @param {number} maxDecibels This argument is parameter for spectrogram from dB. The default value is -30 dB.
   * @param {number} minDecibels This argument is parameter for spectrogram from dB. The default value is -100 dB.
   * @return {string} Return value is color string.
   */
  public static numberToJetColor(data: Uint8Array[0] | Float32Array[0], type: DataType, maxDecibels?: number, minDecibels?: number): string {
    switch (type) {
      case 'uint': {
        const rgba = 4 * (data / 255);

        const r = Math.max(0, Math.min(255, Math.trunc(Math.min((rgba - 1.5), (0 - rgba + 4.5)) * 255)));
        const g = Math.max(0, Math.min(255, Math.trunc(Math.min((rgba - 0.5), (0 - rgba + 3.5)) * 255)));
        const b = Math.max(0, Math.min(255, Math.trunc(Math.min((rgba + 0.5), (0 - rgba + 2.5)) * 255)));

        return `rgb(${r} ${g} ${b})`;
      }

      case 'float': {
        if ((typeof maxDecibels === 'number') && (typeof minDecibels === 'number')) {
          const rgba = 4 * ((1 / (maxDecibels - minDecibels)) * Math.max(0, (data - minDecibels)));

          const r = Math.max(0, Math.min(255, Math.trunc(Math.min((rgba - 1.5), (0 - rgba + 4.5)) * 255)));
          const g = Math.max(0, Math.min(255, Math.trunc(Math.min((rgba - 0.5), (0 - rgba + 3.5)) * 255)));
          const b = Math.max(0, Math.min(255, Math.trunc(Math.min((rgba + 0.5), (0 - rgba + 2.5)) * 255)));

          return `rgb(${r} ${g} ${b})`;
        }

        return 'rgb(0 0 0)';
      }
    }
  }

  /**
   * @param {number} sampleRate This argument is sample rate.
   * @param {ChannelNumber} channel This argument is channel number (Left: 0, Right: 1 ...).
   * @param {AnalyserNode} analyser This argument is instance of `AnalyserNode`.
   */
  constructor(sampleRate: number, channel: ChannelNumber, analyser: AnalyserNode) {
    super(sampleRate, channel, analyser);

    this.interval = 40;
  }

  /**
   * This method gets or sets parameters for visualizing spectrogram.
   * This method is overloaded for type interface and type check.
   * @param {keyof SpectrogramParams|SpectrogramParams} params This argument is string if getter. Otherwise, setter.
   * @return {SpectrogramParams[keyof SpectrogramParams]|Spectrogram} Return value is parameter for visualizing spectrogram if getter.
   *     Otherwise, return value is for method chain.
   * @override
   */
  public override param(params: 'interval'): number;
  public override param(params: 'styles'): GraphicsStyles;
  public override param(params: 'type'): DataType;
  public override param(params: 'size'): number;
  public override param(params: 'scale'): SpectrumScale;
  public override param(params: 'duration'): number;
  public override param(params: 'plotInterval'): number;
  public override param(params: 'linearFrequencyTextInterval'): number;
  public override param(params: 'timeTextInterval'): number;
  public override param(params: 'logarithmicFrequencies'): number[];
  public override param(params: 'minFrequency'): number;
  public override param(params: 'maxFrequency'): number;
  public override param(params: SpectrogramParams): Spectrogram;
  public override param(params: keyof SpectrogramParams | SpectrogramParams): SpectrogramParams[keyof SpectrogramParams] | Spectrogram {
    if (typeof params === 'string') {
      switch (params) {
        case 'type': {
          return this.type;
        }

        case 'size': {
          return this.renderSize;
        }

        case 'scale': {
          return this.scale;
        }

        case 'duration': {
          return this.duration;
        }

        case 'plotInterval': {
          return this.plotInterval;
        }

        case 'linearFrequencyTextInterval': {
          return this.linearFrequencyTextInterval;
        }

        case 'timeTextInterval': {
          return this.timeTextInterval;
        }

        case 'logarithmicFrequencies': {
          return this.logarithmicFrequencies;
        }

        case 'minFrequency': {
          return this.minFrequency;
        }

        case 'maxFrequency': {
          return this.maxFrequency;
        }

        case 'interval': {
          return super.param(params);
        }

        case 'styles': {
          return super.param(params);
        }
      }
    }

    for (const [key, value] of Object.entries(params)) {
      switch (key) {
        case 'type': {
          if (typeof value === 'string') {
            if ((value === 'uint') || (value === 'float')) {
              this.type = value;
            }
          }

          break;
        }

        case 'size': {
          if (typeof value === 'number') {
            this.renderSize = value;
          }

          break;
        }

        case 'scale': {
          if (typeof value === 'string') {
            if ((value === 'linear') || (value === 'logarithmic')) {
              this.scale = value;
            }
          }

          break;
        }

        case 'duration': {
          if (typeof value === 'number') {
            if (value > 0) {
              this.duration = value;
            }
          }

          break;
        }

        case 'plotInterval': {
          if (typeof value === 'number') {
            this.plotInterval = value;
          }

          break;
        }

        case 'linearFrequencyTextInterval': {
          if (typeof value === 'number') {
            if (value > 0) {
              this.linearFrequencyTextInterval = value;
            }
          }

          break;
        }

        case 'timeTextInterval': {
          if (typeof value === 'number') {
            if (value > 0) {
              this.timeTextInterval = value;
            }
          }

          break;
        }

        case 'logarithmicFrequencies': {
          if (Array.isArray(value)) {
            this.logarithmicFrequencies = value;

            this.minFrequency = value[0];
            this.maxFrequency = value[value.length - 1];
            this.ratio        = this.maxFrequency / this.minFrequency;
          }

          break;
        }
      }
    }

    super.param(params);

    return this;
  }

  /**
   * This method sets function that converts number to color string.
   * @param {function} func This argument is function that converts number to color string;
   * @return {Spectrogram} Return value is for method chain.
   */
  public setColorFromNumberFunction(func: ((data: Uint8Array[0] | Float32Array[0]) => string) | null): Spectrogram {
    this.colorFromNumber = func;

    return this;
  }

  /**
   * This method visualizes spectrogram to Canvas.
   * @param {Uint8Array|Float32Array} data This argument is frequency domain data for spectrogram.
   * @override
   */
  protected override renderCanvas(data: Uint8Array | Float32Array): void {
    if ((this.canvas === null) || (this.context === null) || !this.isActive) {
      return;
    }

    const context = this.context;

    const width       = this.canvas.width;
    const height      = this.canvas.height;
    const top         = this.styles.top ?? 0;
    const bottom      = this.styles.bottom ?? 0;
    const left        = this.styles.left ?? 30;
    const right       = this.styles.right ?? 30;
    const innerWidth  = width  - (left + right);
    const innerHeight = height - (top  + bottom);

    const gridColor = this.styles.grid ?? 'none';
    const textColor = this.styles.text ?? 'none';

    const frequencyBinCount = data.length;
    const fftSize           = 2 * frequencyBinCount;

    // Frequency resolution (Sampling rate / FFT size)
    const frequencyResolution = this.sampleRate / fftSize;

    const samples = this.duration * this.sampleRate;

    if (this.numberOfSamples >= samples) {
      this.imagedata = null;

      this.timeOffset      = 1;
      this.numberOfSamples = 0;
    }

    if (this.imagedata === null) {
      // Erase previous wave
      context.clearRect(0, 0, width, height);

      // Render Graph
      context.fillStyle = gridColor;
      context.fillRect(left, (top + innerHeight), innerWidth, 2);
      context.fillRect(left, top, 2, innerHeight);

      // Render coordinate
      context.fillStyle = textColor;
      context.font      = this.createFontString();

      // X axis (time)
      context.textAlign = 'start';

      const textInterval = samples / this.timeTextInterval;

      for (let k = 0; k < samples; k++) {
        if ((k % textInterval) !== 0) {
          continue;
        }

        const x = k * (innerWidth / samples) + left;
        const y = top + innerHeight + Number.parseInt((this.styles.font?.size ?? '13'), 10);

        const time = (k / samples) * this.duration;

        if ((time !== 0) && (time < 0.0001)) {
          context.fillText(`${(time * 1000).toFixed(1)} msec`, x, y);
        } else {
          context.fillText(`${time.toFixed(2)} sec`, x, y);
        }
      }

      context.textAlign = 'end';

      // Y axis (frequency)
      switch (this.scale) {
        case 'linear': {
          const length = Math.min(frequencyBinCount, this.renderSize);

          for (let k = 0; k <= length; k++) {
            if ((k % this.linearFrequencyTextInterval) !== 0) {
              continue;
            }

            const x = left;
            const y = ((1 - (k / length)) * innerHeight) + top;

            const frequency = k * frequencyResolution;

            context.fillText(`${Math.trunc(frequency)} Hz`, x, y);
          }

          break;
        }

        case 'logarithmic': {
          this.logarithmicFrequencies.forEach((frequency: number, index: number) => {
            const x = left;
            const y = ((1 - (index / this.logarithmicFrequencies.length)) * innerHeight) + top;

            if (frequency >= 1000) {
              context.fillText(`${Math.trunc(frequency / 1000)} kHz`, x, y);
            } else {
              context.fillText(`${frequency} Hz`, x, y);
            }
          });

          break;
        }
      }
    } else {
      context.putImageData(this.imagedata, 0, 0);
    }

    // Render spectrogram
    switch (this.scale) {
      case 'linear': {
        const length = Math.min(frequencyBinCount, this.renderSize);

        const h = Number.parseInt((this.styles.font?.size ?? '13'), 10);

        for (let k = 0; k < length; k++) {
          const frequency = k * frequencyResolution;

          if ((frequency < this.minFrequency) || (frequency > this.maxFrequency)) {
            continue;
          }

          const x = left + this.timeOffset;
          const y = ((1 - (k / length)) * innerHeight) + top;

          if (this.colorFromNumber === null) {
            const color = Spectrogram.numberToJetColor(data[k], this.type, this.analyser?.maxDecibels, this.analyser?.minDecibels);

            context.fillStyle = color;
            context.fillRect(x, (y - h), 1, h);
          } else {
            const color = this.colorFromNumber(data[k]);

            context.fillStyle = color;
            context.fillRect(x, (y - h), 1, h);
          }
        }

        break;
      }

      case 'logarithmic': {
        const log10Ratio = Math.log10(this.ratio);

        const h = innerHeight / (this.logarithmicFrequencies.length - 1);

        for (let k = 0; k < frequencyBinCount; k++) {
          if (k === 0) {
            continue;
          }

          if ((k !== 1) && ((k % this.plotInterval) !== 0)) {
            continue;
          }

          const frequency = k * frequencyResolution;

          if ((frequency < this.minFrequency) || (frequency > this.maxFrequency)) {
            continue;
          }

          const frequencyRatio      = frequency / this.minFrequency;
          const log10FrequencyRatio = Math.log10(frequencyRatio);

          const x = left + this.timeOffset;
          const y = ((1 - (log10FrequencyRatio / log10Ratio)) * innerHeight) + top;

          if (y < top) {
            continue;
          }

          if (this.colorFromNumber === null) {
            const color = Spectrogram.numberToJetColor(data[k], this.type, this.analyser?.maxDecibels, this.analyser?.minDecibels);

            context.fillStyle = color;
            context.fillRect(x, (y > (top + innerHeight - h) ? (top + innerHeight - h) : y), 1, h);
          } else {
            const color = this.colorFromNumber(data[k]);

            context.fillStyle = color;
            context.fillRect(x, (y > (top + innerHeight - h) ? (top + innerHeight - h) : y), 1, h);
          }
        }

        break;
      }
    }

    // Render time text
    context.fillStyle = textColor;
    context.font      = this.createFontString();
    context.textAlign = 'center';

    this.numberOfSamples += frequencyBinCount;

    this.timeOffset = (this.numberOfSamples * (1 / this.sampleRate)) * (innerWidth / this.duration);

    this.imagedata = context.getImageData(0, 0, width, height);
  }

  /**
   * This method visualizes spectrogram to SVG.
   * @param {Uint8Array|Float32Array} data This argument is frequency domain data for spectrogram.
   * @override
   */
  protected override renderSVG(data: Uint8Array | Float32Array): void {
    if ((this.svg === null) || !this.isActive) {
      return;
    }

    const svg = this.svg;

    const width       = Number((svg.getAttribute('width') ?? '0'));
    const height      = Number((svg.getAttribute('height') ?? '0'));
    const top         = this.styles.top ?? 0;
    const bottom      = this.styles.bottom ?? 0;
    const left        = this.styles.left ?? 30;
    const right       = this.styles.right ?? 30;
    const innerWidth  = width  - (left + right);
    const innerHeight = height - (top  + bottom);

    const gridColor = this.styles.grid ?? 'none';
    const textColor = this.styles.text ?? 'none';
    const fontSize  = Number.parseInt((this.styles.font?.size ?? '13px'), 10);

    const frequencyBinCount = data.length;
    const fftSize           = 2 * frequencyBinCount;

    // Frequency resolution (Sampling rate / FFT size)
    const frequencyResolution = this.sampleRate / fftSize;

    const samples = this.duration * this.sampleRate;

    if (this.numberOfSamples >= samples) {
      svg.innerHTML = '';

      this.timeOffset      = 1;
      this.numberOfSamples = 0;
    }

    if (svg.innerHTML.length === 0) {
      // Render Graph
      const rectX = document.createElementNS(Spectrogram.XMLNS, 'rect');

      rectX.setAttribute('x', left.toString(10));
      rectX.setAttribute('y', (top + innerHeight).toString(10));
      rectX.setAttribute('width', innerWidth.toString(10));
      rectX.setAttribute('height', '2');
      rectX.setAttribute('fill', gridColor);
      rectX.setAttribute('stroke', 'none');

      const rectY = document.createElementNS(Spectrogram.XMLNS, 'rect');

      rectY.setAttribute('x', left.toString(10));
      rectY.setAttribute('y', top.toString(10));
      rectY.setAttribute('width', '2');
      rectY.setAttribute('height', innerHeight.toString(10));
      rectY.setAttribute('fill', gridColor);
      rectY.setAttribute('stroke', 'none');

      svg.appendChild(rectX);
      svg.appendChild(rectY);

      // Render coordinate

      // X axis (time)
      const g = document.createElementNS(Spectrogram.XMLNS, 'g');

      const textInterval = samples / this.timeTextInterval;

      for (let k = 0; k < samples; k++) {
        if ((k % textInterval) !== 0) {
          continue;
        }

        const x = k * (innerWidth / samples) + left;
        const y = top + innerHeight + fontSize;

        const text = document.createElementNS(Spectrogram.XMLNS, 'text');

        const time = (k / samples) * this.duration;

        if ((time !== 0) && (time < 0.0001)) {
          text.textContent = `${(time * 1000).toFixed(1)} msec`;
        } else {
          text.textContent = `${time.toFixed(2)} sec`;
        }

        text.setAttribute('x', x.toString(10));
        text.setAttribute('y', y.toString(10));
        text.setAttribute('text-anchor', 'start');
        text.setAttribute('stroke', 'none');
        text.setAttribute('fill', textColor);
        text.setAttribute('font-size', fontSize.toString(10));

        g.appendChild(text);
      }

      svg.appendChild(g);

      // Y axis (frequency)
      switch (this.scale) {
        case 'linear': {
          const length = Math.min(frequencyBinCount, this.renderSize);

          const g = document.createElementNS(Spectrogram.XMLNS, 'g');

          for (let k = 0; k <= length; k++) {
            if ((k % this.linearFrequencyTextInterval) !== 0) {
              continue;
            }

            const x = left;
            const y = ((1 - (k / length)) * innerHeight) + top;

            const frequency = k * frequencyResolution;

            const text = document.createElementNS(Spectrogram.XMLNS, 'text');

            text.textContent = `${Math.trunc(frequency)} Hz`;

            text.setAttribute('x', x.toString(10));
            text.setAttribute('y', y.toString(10));
            text.setAttribute('text-anchor', 'end');
            text.setAttribute('stroke', 'none');
            text.setAttribute('fill', textColor);
            text.setAttribute('font-size', fontSize.toString(10));

            g.appendChild(text);
          }

          svg.appendChild(g);

          break;
        }

        case 'logarithmic': {
          const g = document.createElementNS(Spectrogram.XMLNS, 'g');

          this.logarithmicFrequencies.forEach((frequency: number, index: number) => {
            const x = left;
            const y = ((1 - (index / this.logarithmicFrequencies.length)) * innerHeight) + top;

            const text = document.createElementNS(Spectrogram.XMLNS, 'text');

            if (frequency >= 1000) {
              text.textContent = `${Math.trunc(frequency / 1000)} kHz`;
            } else {
              text.textContent = `${frequency} Hz`;
            }

            text.setAttribute('x', x.toString(10));
            text.setAttribute('y', y.toString(10));
            text.setAttribute('text-anchor', 'end');
            text.setAttribute('stroke', 'none');
            text.setAttribute('fill', textColor);
            text.setAttribute('font-size', fontSize.toString(10));

            g.appendChild(text);
          });

          svg.appendChild(g);

          break;
        }
      }
    }

    // Render spectrogram
    switch (this.scale) {
      case 'linear': {
        const length = Math.min(frequencyBinCount, this.renderSize);

        const h = Number.parseInt((this.styles.font?.size ?? '13'), 10);

        const g = document.createElementNS(Spectrogram.XMLNS, 'g');

        for (let k = 0; k < length; k++) {
          const frequency = k * frequencyResolution;

          if ((frequency < this.minFrequency) || (frequency > this.maxFrequency)) {
            continue;
          }

          const x = left + this.timeOffset;
          const y = ((1 - (k / length)) * innerHeight) + top;

          const rect = document.createElementNS(Spectrogram.XMLNS, 'rect');

          if (this.colorFromNumber === null) {
            const color = Spectrogram.numberToJetColor(data[k], this.type, this.analyser?.maxDecibels, this.analyser?.minDecibels);

            rect.setAttribute('fill', color);
          } else {
            const color = this.colorFromNumber(data[k]);

            rect.setAttribute('fill', color);
          }

          rect.setAttribute('x', x.toString(10));
          rect.setAttribute('y', (y - h).toString(10));
          rect.setAttribute('width', (this.styles.width ? this.styles.width.toString(10) : '1'));
          rect.setAttribute('height', h.toString(10));
          rect.setAttribute('stroke', 'none');

          g.appendChild(rect);
        }

        svg.appendChild(g);

        break;
      }

      case 'logarithmic': {
        const g = document.createElementNS(Spectrogram.XMLNS, 'g');

        const log10Ratio = Math.log10(this.ratio);

        const h = innerHeight / (this.logarithmicFrequencies.length - 1);

        for (let k = 0; k < frequencyBinCount; k++) {
          if (k === 0) {
            continue;
          }

          if ((k !== 1) && ((k % this.plotInterval) !== 0)) {
            continue;
          }

          const frequency = k * frequencyResolution;

          if ((frequency < this.minFrequency) || (frequency > this.maxFrequency)) {
            continue;
          }

          const frequencyRatio      = frequency / this.minFrequency;
          const log10FrequencyRatio = Math.log10(frequencyRatio);

          const x = left + this.timeOffset;
          const y = ((1 - (log10FrequencyRatio / log10Ratio)) * innerHeight) + top;

          if (y < top) {
            continue;
          }

          const rect = document.createElementNS(Spectrogram.XMLNS, 'rect');

          if (this.colorFromNumber === null) {
            const color = Spectrogram.numberToJetColor(data[k], this.type, this.analyser?.maxDecibels, this.analyser?.minDecibels);

            rect.setAttribute('fill', color);
          } else {
            const color = this.colorFromNumber(data[k]);

            rect.setAttribute('fill', color);
          }

          rect.setAttribute('x', x.toString(10));
          rect.setAttribute('y', (y > (top + innerHeight - h) ? (top + innerHeight - h) : y).toString(10));
          rect.setAttribute('width', (this.styles.width ? this.styles.width.toString(10) : '1'));
          rect.setAttribute('height', h.toString(10));
          rect.setAttribute('stroke', 'none');

          g.appendChild(rect);
        }

        svg.appendChild(g);

        break;
      }
    }


    this.numberOfSamples += frequencyBinCount;

    this.timeOffset = (this.numberOfSamples * (1 / this.sampleRate)) * (innerWidth / this.duration);
  }
}
